import { Page, Text, View, Image, Document, StyleSheet, Font } from '@react-pdf/renderer';
import { Dict } from '@chakra-ui/utils';
import { useTranslation } from 'react-i18next';
import { FC } from 'react';
import SDGImage from '../../Portfolio/Pdf/SDGBase64Logo';
import PDFPortableText from './PDFPortableText';
import { getText } from 'utils/sanity';
import { getCountryWithCity } from 'utils/project';
import RobotoRegular from 'assets/fonts/Roboto-Regular.ttf';
import RobotoBold from 'assets/fonts/Roboto-Bold.ttf';

//region styled
if (!Font.getRegisteredFontFamilies().includes('Roboto')) {
  //@ts-ignore
  Font.register({
    family: 'Roboto',
    format: 'truetype',
    fonts: [{ src: RobotoRegular }, { src: RobotoBold, fontWeight: 'bold' }],
  });
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
  },
  section: {
    paddingHorizontal: 18,
    paddingVertical: 0,
    flexGrow: 1,
  },
  sdgImage: {
    width: 120,
    height: 15,
    objectFit: 'contain',
  },
  projectTitle: {
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    fontSize: 23,
    marginBottom: 5,
    textAlign: 'center',
    paddingTop: 25,
  },
  subTitle: {
    fontFamily: 'Roboto',
    fontWeight: 'normal',
    fontSize: 14,
    textAlign: 'center',
  },
  mainImage: {
    width: '100%',
    height: 120,
    objectFit: 'cover',
    borderRadius: 2,
    marginTop: 20,
    marginBottom: 10,
  },
  certificates: {
    width: '100%',
    fontFamily: 'Roboto',
    flexDirection: 'column',
    marginTop: 25,
    marginBottom: 45,
    paddingLeft: 22,
    paddingRight: 22,
  },
  certificate: {
    flexDirection: 'column',
    width: '25%',
    paddingLeft: 10,
    paddingRight: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  certificateImage: {
    width: 68,
    height: 23,
    objectFit: 'contain',
    marginBottom: 10,
  },
  sdg: {
    width: '100%',
    fontFamily: 'Roboto',
    flexDirection: 'column',
  },
  description: {
    flexDirection: 'column',
    fontFamily: 'Roboto',
    padding: 22,
    borderRadius: 4,
  },
  sdgList: {
    flexDirection: 'column',
    marginLeft: 11,
    marginTop: 20,
  },
  sdgItem: {
    position: 'relative',
    paddingLeft: 24,
    paddingRight: 10,
    paddingVertical: 10,
    flexDirection: 'column',
    borderRadius: 2,
    marginBottom: 8,
  },
  sdgItemWrapper: {
    width: 26,
    height: 26,
    borderRadius: '50%',
    borderWidth: 1.5,
    position: 'absolute',
    left: -13,
    top: 8,
  },
  sdgItemImage: {
    width: 23,
    height: 23,
    objectFit: 'contain',
  },
  projectSDGImage: {
    width: 22,
    height: 22,
    borderRadius: 2,
    objectFit: 'cover',
    marginRight: 10,
  },
});
//endregion

interface Props {
  theme: Dict;
  project?: Project;
}

const ProjectInfo: FC<Props> = ({ theme, project }) => {
  const { t } = useTranslation('projects');
  const { colors } = theme;
  const benefitsAvailable = !!(project?.benefits && project?.benefits.length);
  const whySectionAvailable = !!(project?.whyThisProject && project?.whyThisProject.length);
  const howItWorksSectionAvailable = !!(project?.howItWorks && project?.howItWorks.length);

  return (
    <Document>
      <Page size="A4" style={[styles.page, { backgroundColor: colors.white }]}>
        <View style={styles.section}>
          <Text style={[styles.projectTitle, { color: colors.secondary[800] }]}>{project?.title}</Text>
          <Text style={[styles.subTitle, { color: colors.secondary[500] }]}>{getCountryWithCity(project)}</Text>
          <Image src={project?.imageUrl ?? ''} style={styles.mainImage} />
          {(project?.subtitle || project?.description || project?.projectOwner) && (
            <View style={[styles.description, { backgroundColor: colors.secondary[100] }]}>
              {project?.subtitle && (
                <Text style={{ color: colors.secondary[800], fontWeight: 'bold', fontSize: 13, marginBottom: 15 }}>
                  {project?.subtitle || project?.title}
                </Text>
              )}
              {project?.description && <Text style={{ color: colors.secondary[800], fontSize: 9 }}>{getText(project?.description)}</Text>}
              {project?.projectOwner && (
                <View style={{ marginTop: 15, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text style={{ color: colors.secondary[500], fontSize: 11 }}>{t('projectOwner')}</Text>
                  <Text style={{ color: colors.secondary[800], fontWeight: 'bold', fontSize: 11 }}>{project?.projectOwner}</Text>
                </View>
              )}
            </View>
          )}
          {project?.certifiedBy.length && (
            <View style={styles.certificates}>
              <Text style={{ fontSize: 12, fontWeight: 'bold', color: colors.secondary[800], marginBottom: 15 }}>{t('certifications')}</Text>
              <View style={{ width: '100%', flexDirection: 'row', marginLeft: -10, marginRight: -10, flexWrap: 'wrap' }} wrap={false}>
                {project?.certifiedBy.map(item => (
                  <View key={item.title} style={styles.certificate}>
                    <Image src={item.logoUrl ?? ''} style={styles.certificateImage} />
                    <Text style={{ fontSize: 7, color: colors.secondary[700], flexWrap: 'wrap' }}>{item.description}</Text>
                  </View>
                ))}
              </View>
            </View>
          )}
          {benefitsAvailable && (
            <View style={[styles.description, { backgroundColor: colors.secondary[100], marginBottom: 15 }]}>
              <View
                style={{
                  flexDirection: 'column',
                  width: '100%',
                  color: colors.secondary[700],
                  fontFamily: 'Roboto',
                  fontSize: 9,
                }}
              >
                <Text style={{ fontSize: 12, fontWeight: 'bold', color: colors.secondary[800], marginBottom: 6 }}>{t('benefits')}</Text>
                <PDFPortableText blocks={project?.benefits ?? []} />
              </View>
            </View>
          )}
          {(whySectionAvailable || howItWorksSectionAvailable) && (
            <View style={[styles.description, { backgroundColor: colors.secondary[100], marginBottom: 15 }]} wrap={false}>
              <View style={{ flexDirection: 'row', width: '100%', color: colors.secondary[700], fontFamily: 'Roboto', fontSize: 9 }}>
                {whySectionAvailable && (
                  <View
                    style={{
                      flexDirection: 'column',
                      width: howItWorksSectionAvailable ? '40%' : '100%',
                      paddingRight: howItWorksSectionAvailable ? 10 : 0,
                    }}
                  >
                    <Text style={{ fontSize: 12, fontWeight: 'bold', color: colors.secondary[800], marginBottom: 6 }}>{t('whyThisProject')}</Text>
                    <PDFPortableText blocks={project?.whyThisProject ?? []} />
                  </View>
                )}
                {howItWorksSectionAvailable && (
                  <View style={{ flexDirection: 'column', width: whySectionAvailable ? '60%' : '100%', paddingLeft: whySectionAvailable ? 25 : 0 }}>
                    <Text style={{ fontSize: 12, fontWeight: 'bold', color: colors.secondary[800], marginBottom: 6 }}>{t('howItWorks')}</Text>
                    <PDFPortableText blocks={project?.howItWorks ?? []} />
                  </View>
                )}
              </View>
            </View>
          )}
          {project?.sdgs.length && (
            <View style={[styles.sdg, { marginTop: 30, marginBottom: 15 }]}>
              <View style={{ flexDirection: 'row', width: '100%', justifyContent: 'space-between' }}>
                <View style={{ flexDirection: 'column', maxWidth: '70%' }}>
                  <Text style={{ fontSize: 12, fontWeight: 'bold', color: colors.secondary[800], marginBottom: 6 }}>{t('portfolioSDG')}</Text>
                  <Text style={{ fontSize: 8, color: colors.secondary[700] }}>{t('portfolioSDGDetails')}</Text>
                </View>
                <Image src={SDGImage} style={styles.sdgImage} />
              </View>
              <View style={styles.sdgList}>
                {project?.sdgs.map(
                  sdg =>
                    sdg.logoUrl && (
                      <View key={sdg.id} style={[styles.sdgItem, { backgroundColor: colors.secondary[200] }]} wrap={false}>
                        <View style={[styles.sdgItemWrapper, { borderColor: colors.white }]}>
                          <Image src={sdg.logoUrl ?? ''} style={styles.sdgItemImage} />
                        </View>
                        <View style={{ flexDirection: 'row', width: '100%' }}>
                          <Text
                            style={{
                              width: '27%',
                              fontFamily: 'Roboto',
                              fontWeight: 'bold',
                              color: colors.secondary[800],
                              fontSize: 10,
                              paddingRight: 5,
                            }}
                          >
                            {sdg.title}
                          </Text>
                          <View style={{ flexDirection: 'column', width: '73%', paddingLeft: 5 }}>
                            <Text style={{ fontWeight: 'bold', fontFamily: 'Roboto', color: colors.secondary[800], fontSize: 8, marginBottom: 12 }}>
                              {sdg.subtitle}
                            </Text>
                            <Text style={{ fontWeight: 'normal', fontFamily: 'Roboto', color: colors.secondary[800], fontSize: 8 }}>
                              {getText(sdg.description)}
                            </Text>
                          </View>
                        </View>
                        <View
                          style={{ width: '100%', flexDirection: 'row', marginTop: 12, padding: 10, backgroundColor: colors.white, flexWrap: 'wrap' }}
                        >
                          <Image src={project?.imageUrl ?? ''} style={styles.projectSDGImage} />
                          <Text
                            style={{
                              fontWeight: 'normal',
                              fontFamily: 'Roboto',
                              color: colors.secondary[800],
                              fontSize: 8,
                              width: '90%',
                              flexWrap: 'wrap',
                            }}
                          >
                            <Text style={{ fontWeight: 'bold' }}>{project?.title}:</Text> {getText(sdg.affect)}
                          </Text>
                        </View>
                      </View>
                    ),
                )}
              </View>
            </View>
          )}
        </View>
      </Page>
    </Document>
  );
};

export default ProjectInfo;
